import MetaTags from "react-meta-tags"
import React, { useEffect } from "react"
import {
  getRoleUser,
  getValueEnum,
  getCurrentUser,
} from "helpers/service_helper"
import { createImageFromInitials, getRandomColor } from "helpers/create_avatar"
import {
  Card,
  Form,
  Input,
  Button,
  Avatar,
  Tag,
  Typography,
  Row,
  Col,
  Space,
  Divider,
} from "antd"
import {
  UserOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  IdcardOutlined,
} from "@ant-design/icons"
import { withTranslation } from "react-i18next"
import { connect } from "react-redux"
import {
  changePassword,
  getFileCommonReset,
  getFilesCommon,
} from "../../store/actions"
import { rolesType } from "common/enums/rolesType"

const { Title, Text } = Typography

const roleColors = {
  Administrator: "red",
  Executor: "blue",
  Engineer: "green",
  Installer: "orange",
  Nezarat: "purple",
  Accountant: "cyan",
  Employee: "gold",
}

const UserProfile = props => {
  const { fileByte, signatureFileName } = props
  const [form] = Form.useForm()
  const currentUser = getCurrentUser()
  const roles = getRoleUser()

  useEffect(() => {
    const { onGetFilesCommon } = props
    onGetFilesCommon({
      folder: "Signature",
      path: `signature-${currentUser.sid}`,
    })
  }, [fileByte, signatureFileName])

  const handleChangePassword = values => {
    const { onPasswordChange } = props
    onPasswordChange(values)
    form.resetFields()
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>پروفایل | واحد گاز</title>
        </MetaTags>

        <Row gutter={[24, 24]} style={{ maxWidth: 900, margin: "0 auto" }}>
          {/* Profile Info Card */}
          <Col span={24}>
            <Card
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              }}
            >
              <Space align="center" size={24}>
                <Avatar
                  size={72}
                  src={createImageFromInitials(72, currentUser.name, getRandomColor())}
                  icon={<UserOutlined />}
                  style={{ flexShrink: 0 }}
                />
                <div>
                  <Title level={4} style={{ margin: 0 }}>
                    {currentUser.name}
                  </Title>
                  <Space size={6} style={{ marginTop: 8 }} wrap>
                    <IdcardOutlined style={{ color: "#8c8c8c" }} />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      نقش‌های شما:
                    </Text>
                    {roles.map((item, index) => {
                      const label = getValueEnum(rolesType, item)?.value || item
                      return (
                        <Tag
                          key={index}
                          color={roleColors[item] || "default"}
                          style={{ borderRadius: 8 }}
                        >
                          {label}
                        </Tag>
                      )
                    })}
                  </Space>
                </div>
              </Space>

              {/* Signature preview */}
              {fileByte && (
                <>
                  <Divider style={{ margin: "16px 0" }} />
                  <div>
                    <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                      <SafetyCertificateOutlined style={{ marginLeft: 6 }} />
                      امضای ثبت‌شده
                    </Text>
                    <img
                      src={`data:image/png;base64,${fileByte}`}
                      alt="امضا"
                      style={{
                        maxHeight: 80,
                        borderRadius: 8,
                        border: "1px solid #f0f0f0",
                        padding: 8,
                        background: "#fafafa",
                      }}
                    />
                  </div>
                </>
              )}
            </Card>
          </Col>

          {/* Change Password Card */}
          <Col span={24}>
            <Card
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              }}
              title={
                <Space>
                  <LockOutlined style={{ color: "#1890ff" }} />
                  <span>تغییر رمز عبور</span>
                </Space>
              }
            >
              <Text
                type="secondary"
                style={{ display: "block", marginBottom: 20, fontSize: 13 }}
              >
                رمز جدید باید حداقل ۸ کاراکتر و ترکیبی از حرف کوچک، حرف بزرگ و
                عدد باشد — مثال:{" "}
                <Text code>bAbcd123</Text>
              </Text>

              <Form
                form={form}
                layout="vertical"
                onFinish={handleChangePassword}
                style={{ maxWidth: 420 }}
                requiredMark={false}
              >
                <Form.Item
                  name="oldPassword"
                  label="رمز فعلی"
                  rules={[{ required: true, message: "رمز فعلی الزامی است" }]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: "#bfbfbf" }} />}
                    placeholder="رمز فعلی خود را وارد کنید"
                    size="large"
                    style={{ borderRadius: 8 }}
                  />
                </Form.Item>

                <Form.Item
                  name="newPassword"
                  label="رمز جدید"
                  rules={[
                    { required: true, message: "رمز جدید الزامی است" },
                    { min: 8, message: "حداقل ۸ کاراکتر" },
                    { max: 16, message: "حداکثر ۱۶ کاراکتر" },
                    {
                      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/,
                      message: "باید شامل حرف کوچک، حرف بزرگ و عدد باشد",
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<SafetyCertificateOutlined style={{ color: "#bfbfbf" }} />}
                    placeholder="رمز جدید را وارد کنید"
                    size="large"
                    style={{ borderRadius: 8 }}
                  />
                </Form.Item>

                <Form.Item style={{ marginTop: 24 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    icon={<LockOutlined />}
                    style={{ borderRadius: 8, minWidth: 160 }}
                  >
                    تغییر رمز عبور
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </Col>
        </Row>
      </div>
    </React.Fragment>
  )
}

const mapStateToProps = ({ Executors, Commons }) => ({
  signatureFileName: Executors.signatureFileName,
  fileByte: Commons.fileByte,
})
const mapDispatchToProps = dispatch => ({
  onGetFilesCommon: path => dispatch(getFilesCommon(path)),
  onGetFileCommonReset: () => dispatch(getFileCommonReset()),
  onPasswordChange: data => dispatch(changePassword(data)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(UserProfile))
